import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { AvailableSlot } from '@atomfit/shared';

interface BookingFlowState {
  gymId: string | null;
  selectedSlot: AvailableSlot | null;
  selectedSlots: AvailableSlot[];
  bookingId: string | null;
  bookingGroupId: string | null;
  bookingExpiresAt: string | null;
  stripeAccountId: string | null;
  setStripeAccountId: (id: string | null) => void;
  clientSecret: string | null;
  customerSessionClientSecret: string | null;
  setGymId: (gymId: string) => void;
  setSelectedSlot: (slot: AvailableSlot) => void;
  setSelectedSlots: (slots: AvailableSlot[]) => void;
  setBookingId: (id: string) => void;
  setBookingGroupId: (id: string | null) => void;
  setBookingExpiresAt: (expiresAt: string) => void;
  setClientSecret: (secret: string | null) => void;
  setCustomerSessionClientSecret: (secret: string | null) => void;
  clearPaymentSession: () => void;
  reset: () => void;
}

type PersistedBookingFlowState = Pick<
  BookingFlowState,
  | 'gymId'
  | 'selectedSlot'
  | 'selectedSlots'
  | 'bookingId'
  | 'bookingGroupId'
  | 'bookingExpiresAt'
>;

const bookingFlowStorageKey = 'atomfit-booking-flow';

function getBookingFlowStorage() {
  const existingLocalFlow = localStorage.getItem(bookingFlowStorageKey);
  const existingSessionFlow = sessionStorage.getItem(bookingFlowStorageKey);

  if (!existingLocalFlow && existingSessionFlow) {
    localStorage.setItem(bookingFlowStorageKey, existingSessionFlow);
  }

  return localStorage;
}

export const useBookingStore = create<BookingFlowState>()(
  persist<BookingFlowState, [], [], PersistedBookingFlowState>(
    (set) => ({
      gymId: null,
      selectedSlot: null,
      selectedSlots: [],
      bookingId: null,
      bookingGroupId: null,
      bookingExpiresAt: null,
      stripeAccountId: null,
      clientSecret: null,
      customerSessionClientSecret: null,
      setGymId: (gymId) => set({ gymId }),
      setSelectedSlot: (selectedSlot) =>
        set({
          selectedSlot,
          selectedSlots: [selectedSlot],
          bookingId: null,
          bookingGroupId: null,
          bookingExpiresAt: null,
          stripeAccountId: null,
      clientSecret: null,
          customerSessionClientSecret: null,
        }),
      setSelectedSlots: (selectedSlots) =>
        set({
          selectedSlots,
          selectedSlot: selectedSlots[0] ?? null,
          bookingId: null,
          bookingGroupId: null,
          bookingExpiresAt: null,
          stripeAccountId: null,
      clientSecret: null,
          customerSessionClientSecret: null,
        }),
      setBookingId: (bookingId) => set({ bookingId }),
      setBookingGroupId: (bookingGroupId) => set({ bookingGroupId }),
      setBookingExpiresAt: (bookingExpiresAt) => set({ bookingExpiresAt }),
      setStripeAccountId: (stripeAccountId) => set({ stripeAccountId }),
      setClientSecret: (clientSecret) => set({ clientSecret }),
      setCustomerSessionClientSecret: (customerSessionClientSecret) =>
        set({ customerSessionClientSecret }),
      clearPaymentSession: () =>
        set({
          bookingId: null,
          bookingGroupId: null,
          bookingExpiresAt: null,
          stripeAccountId: null,
      clientSecret: null,
          customerSessionClientSecret: null,
        }),
      reset: () =>
        set({
          gymId: null,
          selectedSlot: null,
          selectedSlots: [],
          bookingId: null,
          bookingGroupId: null,
          bookingExpiresAt: null,
          stripeAccountId: null,
      clientSecret: null,
          customerSessionClientSecret: null,
        }),
    }),
    {
      name: bookingFlowStorageKey,
      storage: createJSONStorage<PersistedBookingFlowState>(getBookingFlowStorage),
      version: 1,
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as Partial<BookingFlowState>;
        return {
          gymId: state.gymId ?? null,
          selectedSlot: state.selectedSlot ?? null,
          selectedSlots: state.selectedSlots ?? [],
          bookingId: state.bookingId ?? null,
          bookingGroupId: state.bookingGroupId ?? null,
          bookingExpiresAt: state.bookingExpiresAt ?? null,
        };
      },
      partialize: (state) => ({
        gymId: state.gymId,
        selectedSlot: state.selectedSlot,
        selectedSlots: state.selectedSlots,
        bookingId: state.bookingId,
        bookingGroupId: state.bookingGroupId,
        bookingExpiresAt: state.bookingExpiresAt,
      }),
    },
  ),
);
